import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Clock } from "lucide-react";
import { useApi, errMsg } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";

const elapsed = (from) => {
  const s = Math.max(0, Math.floor((Date.now() - new Date(from).getTime()) / 1000));
  return [Math.floor(s / 3600), Math.floor((s % 3600) / 60), s % 60].map((n) => String(n).padStart(2, "0")).join(":");
};

export const ClockWidget = ({ onChange }) => {
  const api = useApi();
  const [active, setActive] = useState(undefined);
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState("");
  const [notes, setNotes] = useState("");
  const [, tick] = useState(0);

  const load = useCallback(() => api("get", "/time/active").then(setActive), [api]);
  useEffect(() => {
    load();
    api("get", "/projects").then(setProjects);
  }, [api, load]);
  useEffect(() => {
    const t = setInterval(() => tick((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, []);

  const toggle = async () => {
    try {
      if (active) await api("post", "/time/clock-out");
      else await api("post", "/time/clock-in", { project_id: projectId, notes });
      toast.success(active ? "Clocked out" : "Clocked in");
      setNotes("");
      await load();
      onChange?.();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  if (active === undefined) return null;
  return (
    <div data-testid="clock-widget" className="rounded-2xl border border-[#E6E2DA] bg-white p-6 sm:p-8">
      <div className="flex items-center gap-2"><Clock size={18} className="text-[#A67C00]" /><p className="eyebrow">Time clock</p></div>
      <p data-testid="clock-timer" className="font-mono-num mt-3 text-4xl font-semibold tracking-tight">{active ? elapsed(active.clock_in) : "00:00:00"}</p>
      <p className="mt-1 text-sm text-[#5C5850]" data-testid="clock-status">{active ? `On the clock${active.project_name ? ` · ${active.project_name}` : ""}` : "You're clocked out"}</p>
      {!active && (
        <div className="mt-5 grid gap-3 sm:grid-cols-2">
          <Select value={projectId || "none"} onValueChange={(v) => setProjectId(v === "none" ? "" : v)}>
            <SelectTrigger data-testid="clock-project-select"><SelectValue placeholder="Job" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">No specific job</SelectItem>
              {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input placeholder="Notes (optional)" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="clock-notes-input" />
        </div>
      )}
      <button onClick={toggle} data-testid="clock-in-out-btn" className={`mt-5 w-full rounded-full px-6 py-3.5 text-sm font-bold text-white transition-colors ${active ? "bg-[#0E1F3F] hover:bg-red-700" : "bg-[#F2B91C] !text-[#0E1F3F] hover:bg-[#D9A30F]"}`}>
        {active ? "Clock out" : "Clock in"}
      </button>
    </div>
  );
};
